import { useEffect, useState } from 'react';
import { Ghost, X } from 'lucide-react';
import { cn } from '../../lib/cn';

const STORAGE_PREFIX = 'liveSessionJoinBannerSeen:';
const AUTO_DISMISS_MS = 12000;

/**
 * Soft "heads up — don't leave the tab" banner shown to the student
 * once on their first land in an active live session. Top-center
 * placement mirrors InviteIsland; auto-dismisses after ~12s or on
 * user click. A localStorage flag keyed by `sessionId` prevents the
 * banner from re-appearing on page reloads of the same session.
 *
 * Non-blocking by design — the floating PresencePanel + Ghost cursor
 * swap provide the persistent reinforcement once dismissed.
 */
export default function JoinBanner({ sessionId, active }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (!active || !sessionId) return;
    if (typeof window === 'undefined') return;
    const key = `${STORAGE_PREFIX}${sessionId}`;
    if (window.localStorage?.getItem(key)) return;

    setVisible(true);
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      // localStorage can throw in private-mode or storage-quota cases;
      // banner still shows this session, just may show again on reload.
    }

    const t = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, sessionId]);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
    }, 300);
  };

  if (!visible) return null;

  return (
    <div
      className={cn(
        // Vertically + horizontally centered in the viewport for max
        // prominence on first join. translateY(-50%) anchors the card
        // around its own middle. The RefocusToast lives lower in the
        // viewport so the two never overlap when both are visible.
        'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
        'transition-all duration-300 ease-out',
        exiting
          ? 'opacity-0 scale-95'
          : 'opacity-100 scale-100'
      )}
    >
      <div
        className={cn(
          'flex items-start gap-4 p-6 max-w-md',
          'bg-gray-900 text-white rounded-2xl',
          'dark:bg-white dark:text-gray-900',
          'border border-blue-400/50 dark:border-blue-400/70',
          'shadow-2xl'
        )}
      >
        <div
          className={cn(
            'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
            'bg-blue-500/20 dark:bg-blue-500/15'
          )}
        >
          <Ghost className="w-6 h-6 opacity-90" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold leading-tight mb-1">
            You're in a live session
          </h3>
          <p className="text-sm leading-snug opacity-90">
            If you switch tabs, your cursor turns into a ghost on your
            teacher's screen — try to stay here.
          </p>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className={cn(
            'p-1.5 rounded-full flex-shrink-0 -mt-1 -mr-2',
            'text-white/70 hover:text-white hover:bg-white/10',
            'dark:text-gray-900/70 dark:hover:text-gray-900 dark:hover:bg-gray-900/10',
            'transition-colors'
          )}
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
